import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Users, Zap, Clock, ChevronDown, Search, X, Check, AlertCircle, Share2 } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { isValidModelId, getInvalidModelVoices, getModelValidationMessage } from '../lib/modelValidation';
import { ContextAttacher } from '../components/ContextAttacher';

const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };
const TIER_ORDER = { economy: 0, standard: 1, premium: 2 };

export default function PlaybookDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voices, setVoices] = useState([]);
  const [scenario, setScenario] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState('');
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [debateMode, setDebateMode] = useState('panel');
  const [frictionLevel, setFrictionLevel] = useState(3);
  const [contextIds, setContextIds] = useState([]);
  const [showShareWs, setShowShareWs] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState('');

  useEffect(() => {
    API.get(`/api/playbooks/${slug}`).then(r => {
      setPlaybook(r.data);
      setVoices(r.data.template_config?.voices || []);
    }).catch(() => navigate('/playbooks')).finally(() => setLoading(false));
  }, [slug, navigate]);

  // Load models when user is logged in
  useEffect(() => {
    if (user && availableModels.length === 0) {
      loadModels();
    }
  }, [user]);

  const openShareToWorkspace = () => {
    setShowShareWs(true);
    setShareSuccess('');
    API.get('/api/workspaces').then(r => setWorkspaces(r.data.workspaces || [])).catch(() => setWorkspaces([]));
  };

  const shareToWorkspace = async (wsId) => {
    setSharing(true);
    try {
      await API.post(`/api/workspaces/${wsId}/playbooks`, { playbook_id: playbook?.id });
      setShareSuccess(wsId);
      setTimeout(() => { setShowShareWs(false); setShareSuccess(''); }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Share failed');
    }
    setSharing(false);
  };

  const loadModels = async () => {
    setModelsLoading(true);
    setModelsError('');
    try {
      const res = await API.get('/api/byok/models');
      const sorted = (res.data.models || []).sort((a, b) => {
        const tierDiff = (TIER_ORDER[a.tier] || 0) - (TIER_ORDER[b.tier] || 0);
        if (tierDiff !== 0) return tierDiff;
        return a.name.localeCompare(b.name);
      });
      setAvailableModels(sorted);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('No OpenRouter key')) {
        setModelsError('Add your OpenRouter key in Settings to customize models.');
      } else if (err.response?.status === 401) {
        // Not logged in — that's fine, models will use defaults
      } else {
        setModelsError('Could not load models.');
      }
    }
    setModelsLoading(false);
  };

  const updateVoiceModel = (voiceIndex, modelId, modelName, tier) => {
    setVoices(prev => prev.map((v, i) =>
      i === voiceIndex ? { ...v, default_model: modelId, model_tier: tier, model_name: modelName } : v
    ));
  };

  const handleLaunch = async () => {
    if (!user) { navigate('/login'); return; }
    if (!scenario.trim()) { setError('Describe your scenario before launching'); return; }

    const invalid = getInvalidModelVoices(voices.map(v => ({ ...v, model: v.default_model })));
    if (invalid.length > 0) {
      setError(getModelValidationMessage(invalid));
      return;
    }

    setLaunching(true);
    setError('');
    try {
      const res = await API.post('/api/frays', {
        template_id: playbook.id,
        scenario: scenario,
        voice_configs: voices.map(v => ({ name: v.name, model: v.default_model, mandate: v.mandate })),
        debate_mode: debateMode,
        friction_level: frictionLevel,
        context_ids: contextIds,
      });
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create Fray');
      setLaunching(false);
    }
  };

  if (loading || !playbook) return (
    <div className="min-h-screen bg-[#0F0F0F]"><Header /><div className="pt-24 flex justify-center"><div className="animate-shimmer w-full max-w-3xl h-96" /></div></div>
  );

  const phases = playbook.template_config?.phases || [];

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-4xl mx-auto">
        <div className="mb-2">
          <Link to="/playbooks" className="text-xs text-[#A0A0A0] hover:text-[#FF4D00] font-mono" data-testid="back-to-playbooks">Playbooks /</Link>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-mono text-[#A0A0A0]">{playbook.template_code}</span>
                <span className={`text-[10px] px-2 py-0.5 border font-mono ${playbook.revenue_tier === 'free' ? 'text-[#22C55E] border-[#22C55E]/30' : 'text-[#F59E0B] border-[#F59E0B]/30'}`}>
                  {playbook.revenue_tier === 'free' ? 'FREE' : 'PRO'}
                </span>
                <span className="text-[10px] px-2 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono capitalize">{playbook.domain}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed']" data-testid="playbook-title">{playbook.name}</h1>
            </div>
          </div>

          <p className="text-[#A0A0A0] text-base leading-relaxed mb-8">{playbook.description}</p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-10">
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
              <Users className="w-4 h-4 text-[#FF4D00] mb-2" />
              <span className="text-2xl font-bold font-['Barlow_Condensed']">{voices.length}</span>
              <span className="text-xs text-[#A0A0A0] block mt-1">Voices</span>
            </div>
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
              <Zap className="w-4 h-4 text-[#FF4D00] mb-2" />
              <span className="text-2xl font-bold font-['Barlow_Condensed']">{phases.length}</span>
              <span className="text-xs text-[#A0A0A0] block mt-1">Phases</span>
            </div>
            <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
              <Clock className="w-4 h-4 text-[#FF4D00] mb-2" />
              <span className="text-2xl font-bold font-['Barlow_Condensed']">~5m</span>
              <span className="text-xs text-[#A0A0A0] block mt-1">Duration</span>
            </div>
          </div>

          {/* === SCENARIO INPUT === */}
          <div className="mb-10" data-testid="scenario-section">
            <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-2">Your Scenario</h2>
            <p className="text-sm text-[#A0A0A0] mb-4">Describe the decision you want the committee to evaluate.</p>
            <textarea
              value={scenario}
              onChange={e => setScenario(e.target.value)}
              className="w-full h-36 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm resize-none focus:outline-none focus:border-[#FF4D00] transition-colors leading-relaxed"
              placeholder="e.g., We're building a B2B SaaS tool for AI-powered contract review. Our target market is mid-size law firms..."
              data-testid="scenario-input"
            />
            <span className="text-[10px] font-mono text-[#A0A0A0] mt-1 block">{scenario.length} / 2000 characters</span>
          </div>

          {/* === COMMITTEE VOICES WITH MODEL PICKERS === */}
          <div className="mb-10" data-testid="voices-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold font-['Barlow_Condensed']">Committee Voices</h2>
              {availableModels.length > 0 && (
                <span className="text-[10px] font-mono text-[#22C55E]" data-testid="models-available-count">
                  {availableModels.length} models available
                </span>
              )}
            </div>
            <p className="text-sm text-[#A0A0A0] mb-4">
              {availableModels.length > 0
                ? 'Click the model selector on each Voice to assign a different AI model.'
                : user ? 'Add your OpenRouter key in Settings to customize models per voice.' : 'Log in to customize models per voice.'
              }
            </p>

            {modelsLoading && (
              <div className="p-3 mb-4 bg-[#FF4D00]/5 border border-[#FF4D00]/20 text-sm text-[#FF4D00] flex items-center gap-2">
                <div className="w-3 h-3 border border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
                Loading models from OpenRouter...
              </div>
            )}

            <div className="space-y-3">
              {voices.map((v, i) => (
                <VoiceCardWithPicker
                  key={i}
                  voice={v}
                  index={i}
                  availableModels={availableModels}
                  onSelectModel={(modelId, modelName, tier) => updateVoiceModel(i, modelId, modelName, tier)}
                />
              ))}
            </div>
          </div>

          {/* === PHASES === */}
          <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-4">Phases</h2>
          <div className="space-y-2 mb-10">
            {phases.map((p, i) => (
              <div key={i} className="flex items-start gap-4 p-3 border-l-2 border-[#2A2A2A]" style={{ borderColor: i === phases.length - 1 ? '#FF4D00' : '#2A2A2A' }}>
                <span className="text-xs font-mono text-[#A0A0A0] shrink-0 mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <span className="text-sm font-medium font-['Barlow_Condensed']">{p.name}</span>
                  <p className="text-xs text-[#A0A0A0] mt-1">{p.instructions}</p>
                </div>
              </div>
            ))}
          </div>

          {/* === DEBATE MODE + FRICTION === */}
          <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-3">Debate Mode</h2>
              <div className="space-y-2">
                {[
                  { id: 'panel', name: 'Panel', desc: 'All voices deliberate simultaneously. Fast.', icon: 'FREE' },
                  { id: 'round_robin', name: 'Round-Robin', desc: 'Voices take turns. Real back-and-forth.', icon: 'PRO' },
                  { id: 'dialectic', name: 'Dialectic', desc: 'Two voices go head-to-head, others judge.', icon: 'PRO' },
                  { id: 'cross_examination', name: 'Cross-Exam', desc: 'Each voice challenged by all others.', icon: 'TEAM' },
                ].map(m => (
                  <button key={m.id} onClick={() => setDebateMode(m.id)}
                    className={`w-full p-3 min-h-[44px] text-left border transition-colors ${debateMode === m.id ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#FF4D00]/30'}`}
                    data-testid={`mode-${m.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium font-['Barlow_Condensed']">{m.name}</span>
                      <span className="text-[8px] px-1.5 py-0.5 border border-[#2A2A2A] text-[#A0A0A0] font-mono">{m.icon}</span>
                    </div>
                    <p className="text-xs text-[#A0A0A0] mt-1">{m.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold font-['Barlow_Condensed'] mb-3">Friction Level</h2>
              <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-3xl font-bold font-['Barlow_Condensed']" style={{ color: frictionLevel <= 3 ? '#22C55E' : frictionLevel <= 6 ? '#F59E0B' : frictionLevel <= 8 ? '#FF6B00' : '#EF4444' }}>
                    {frictionLevel}
                  </span>
                  <span className="text-sm font-mono" style={{ color: frictionLevel <= 3 ? '#22C55E' : frictionLevel <= 6 ? '#F59E0B' : frictionLevel <= 8 ? '#FF6B00' : '#EF4444' }}>
                    {frictionLevel <= 2 ? 'Collegial' : frictionLevel <= 4 ? 'Professional' : frictionLevel <= 6 ? 'Assertive' : frictionLevel <= 8 ? 'Adversarial' : 'Combative'}
                  </span>
                </div>
                <input type="range" min="1" max="10" value={frictionLevel} onChange={e => setFrictionLevel(parseInt(e.target.value))}
                  className="w-full accent-[#FF4D00]" data-testid="friction-slider" />
                <div className="flex justify-between text-[8px] text-[#A0A0A0] font-mono mt-1">
                  <span>Collegial</span>
                  <span>Professional</span>
                  <span>Adversarial</span>
                  <span>Max</span>
                </div>
              </div>
            </div>
          </div>

          {/* Context attachment */}
          <ContextAttacher selectedIds={contextIds} onChange={setContextIds} />

          {/* === LAUNCH === */}
          {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="launch-error">{error}</div>}

          <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
            {/* Share to Workspace */}
            {user && playbook && (
              <div className="mb-4 flex items-center gap-2">
                <button
                  onClick={openShareToWorkspace}
                  className="px-3 py-2 min-h-[44px] border border-[#2A2A2A] text-xs font-mono text-[#A0A0A0] hover:text-[#FF4D00] hover:border-[#FF4D00]/30 flex items-center gap-1.5 transition-colors"
                  data-testid="share-to-workspace-btn"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share to Workspace
                </button>
              </div>
            )}

            {showShareWs && (
              <div className="mb-4 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="share-ws-dialog">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-mono text-[#A0A0A0]">SELECT WORKSPACE</h4>
                  <button onClick={() => setShowShareWs(false)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[#A0A0A0]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {workspaces.length === 0 ? (
                  <p className="text-xs text-[#555]">No workspaces found. Create one in <a href="/workspaces" className="text-[#FF4D00] underline">Workspaces</a>.</p>
                ) : (
                  <div className="space-y-2">
                    {workspaces.map(ws => (
                      <button
                        key={ws.id}
                        onClick={() => shareToWorkspace(ws.id)}
                        disabled={sharing}
                        className="w-full p-3 min-h-[44px] flex items-center justify-between border border-[#2A2A2A] hover:border-[#FF4D00]/30 transition-colors disabled:opacity-50"
                        data-testid={`share-ws-${ws.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-[#A0A0A0]" />
                          <span className="text-sm font-['Barlow_Condensed']">{ws.name}</span>
                          <span className="text-[10px] font-mono text-[#555]">{ws.member_count || 0} members</span>
                        </div>
                        {shareSuccess === ws.id && <Check className="w-4 h-4 text-[#22C55E]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-1">Ready?</h3>
                <p className="text-sm text-[#A0A0A0]">
                  {user?.billing_mode === 'byok'
                    ? 'Using your OpenRouter key — no credits consumed'
                    : user ? 'Credits will be deducted from your balance' : 'Sign up or log in to run this Fray'
                  }
                </p>
              </div>
              <button
                onClick={handleLaunch}
                disabled={launching || !scenario.trim()}
                className="w-full sm:w-auto px-8 py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold flex items-center justify-center gap-2 hover:bg-[#FF4D00]/90 transition-colors disabled:opacity-40"
                data-testid="launch-fray-btn"
              >
                {launching ? (
                  <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Sharpening claws...</>
                ) : (
                  <><Zap className="w-4 h-4" /> Enter the Fray</>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Voice Card with inline Model Picker                                 */
/* ------------------------------------------------------------------ */
function VoiceCardWithPicker({ voice, index, availableModels, onSelectModel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = availableModels.filter(m => {
    const matchSearch = !search || m.id.toLowerCase().includes(search.toLowerCase()) || m.name.toLowerCase().includes(search.toLowerCase());
    const matchTier = tierFilter === 'all' || m.tier === tierFilter;
    return matchSearch && matchTier;
  });

  const hasModels = availableModels.length > 0;
  const modelInvalid = !isValidModelId(voice.default_model);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className={`p-4 bg-[#1A1A1A] border ${modelInvalid ? 'border-[#EF4444]/50' : 'border-[#2A2A2A]'}`}
      data-testid={`voice-card-${index}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 flex items-center justify-center border-2 shrink-0 text-sm font-bold font-['Barlow_Condensed']"
          style={{ borderColor: voice.color, color: voice.color }}>
          {voice.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium font-['Barlow_Condensed']">{voice.name}</span>
            {modelInvalid && <span className="text-[8px] px-1.5 py-0.5 bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 font-mono">INVALID MODEL</span>}
          </div>
          <p className="text-sm text-[#A0A0A0] mb-3">{voice.mandate}</p>

          {/* Model Selector */}
          <div className="relative" ref={dropdownRef}>
            {modelInvalid && (
              <div className="text-[9px] text-[#EF4444] font-mono mb-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> "{voice.default_model}" is not a valid model ID — select from dropdown
              </div>
            )}
            <button
              onClick={() => setOpen(!open)}
              className={`w-full flex items-center justify-between px-3 py-2.5 border text-left transition-all group ${
                modelInvalid ? 'border-[#EF4444] bg-[#EF4444]/5' :
                open ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#FF4D00]/50 hover:bg-[#2A2A2A]/30'
              }`}
              data-testid={`model-picker-${index}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[9px] px-1.5 py-0.5 border font-mono shrink-0"
                  style={{ color: TIER_COLORS[voice.model_tier], borderColor: TIER_COLORS[voice.model_tier] + '40' }}>
                  {voice.model_tier?.toUpperCase()}
                </span>
                <span className="text-xs font-mono text-[#F5F5F5] truncate">{voice.default_model}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                <span className="text-[9px] text-[#FF4D00] font-mono opacity-0 group-hover:opacity-100 transition-opacity">
                  {hasModels ? 'change' : 'setup key'}
                </span>
                <ChevronDown className={`w-4 h-4 text-[#A0A0A0] transition-transform ${open ? 'rotate-180 text-[#FF4D00]' : ''}`} />
              </div>
            </button>

            {/* Dropdown */}
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  className="absolute z-50 left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl shadow-black/50 max-h-[320px] flex flex-col"
                  data-testid={`model-dropdown-${index}`}
                >
                  {!hasModels ? (
                    <div className="p-6 text-center">
                      <AlertCircle className="w-5 h-5 text-[#FF4D00] mx-auto mb-2" />
                      <p className="text-sm text-[#F5F5F5] mb-1">No models loaded</p>
                      <p className="text-xs text-[#A0A0A0] mb-3">Add your OpenRouter API key in Settings to browse and select models.</p>
                      <a href="/settings" className="text-xs text-[#FF4D00] hover:underline font-mono">Go to Settings</a>
                    </div>
                  ) : (
                    <>
                      <div className="p-2 border-b border-[#2A2A2A]">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-2.5 top-1/2 -translate-y-1/2" />
                          <input
                            ref={searchRef}
                            type="text" value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full pl-8 pr-8 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                            placeholder="Search models..."
                            data-testid={`model-search-${index}`}
                          />
                          {search && (
                            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <div className="flex gap-1 mt-2">
                          {['all', 'economy', 'standard', 'premium'].map(t => (
                            <button
                              key={t}
                              onClick={() => setTierFilter(t)}
                              className={`px-2 py-0.5 text-[9px] font-mono border transition-colors ${
                                tierFilter === t
                                  ? t === 'all' ? 'bg-[#FF4D00] text-black border-[#FF4D00]'
                                    : `border-current` + (t === 'economy' ? ' text-[#22C55E] bg-[#22C55E]/10' : t === 'standard' ? ' text-[#F59E0B] bg-[#F59E0B]/10' : ' text-[#EF4444] bg-[#EF4444]/10')
                                  : 'border-[#2A2A2A] text-[#A0A0A0]'
                              }`}
                            >{t === 'all' ? 'ALL' : t.toUpperCase()}</button>
                          ))}
                        </div>
                      </div>

                      <div className="overflow-y-auto flex-1">
                        {filtered.length === 0 ? (
                          <div className="p-4 text-center text-xs text-[#A0A0A0]">No models match "{search}"</div>
                        ) : (
                          filtered.slice(0, 50).map((m, mi) => {
                            const isSelected = voice.default_model === m.id;
                            return (
                              <button
                                key={m.id}
                                onClick={() => {
                                  onSelectModel(m.id, m.name, m.tier);
                                  setOpen(false);
                                  setSearch('');
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors border-b border-[#0F0F0F] ${
                                  isSelected ? 'bg-[#FF4D00]/10' : 'hover:bg-[#2A2A2A]/50'
                                }`}
                                data-testid={`model-option-${index}-${mi}`}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span className="text-[8px] w-12 text-center px-1 py-0.5 border font-mono shrink-0"
                                    style={{ color: TIER_COLORS[m.tier], borderColor: TIER_COLORS[m.tier] + '40' }}>
                                    {m.tier?.toUpperCase()?.slice(0, 4)}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs text-[#F5F5F5] block truncate">{m.name || m.id}</span>
                                    <span className="text-[9px] text-[#A0A0A0] font-mono block truncate">{m.id}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 ml-2">
                                  {m.context_length && (
                                    <span className="text-[8px] font-mono text-[#A0A0A0]">{Math.round(m.context_length / 1000)}k ctx</span>
                                  )}
                                  {isSelected && <Check className="w-3.5 h-3.5 text-[#FF4D00]" />}
                                </div>
                              </button>
                            );
                          })
                        )}
                        {filtered.length > 50 && (
                          <div className="p-2 text-center text-[9px] text-[#A0A0A0] font-mono">
                            Showing 50 of {filtered.length} — narrow your search
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
